/**
 * Attributi configurati di un layer, letti dai valori del form.
 * Le sezioni assenti (non configurate) restano `undefined`.
 */
export interface ILayerAttributeSnapshot {
  intersectionAttributes?: string[];
  intersectionAliases?: Record<string, string>;
  identifyAttributes?: string[];
  identifyAliases?: Record<string, string>;
  competenceIdentifyingPropertyName?: string | null;
}

export interface ILayerAttributeDiff {
  invalidIntersectionAttributes: string[];
  invalidIdentifyAttributes: string[];
  isCompetencePropertyInvalid: boolean;
  /** Unione deduplicata dei nomi non validi, per il messaggio all'utente. */
  invalidNames: string[];
  hasInvalid: boolean;
}

/**
 * Patch da applicare al form per rimuovere gli attributi configurati
 * non più disponibili. Contiene solo le chiavi effettivamente cambiate.
 */
export interface ILayerAttributePrunePatch {
  intersectionAttributes?: string[];
  intersectionAliases?: Record<string, string>;
  identifyAttributes?: string[];
  identifyAliases?: Record<string, string>;
  competenceIdentifyingPropertyName?: null;
}

function findInvalidAttributes(
  attributes: string[] | undefined,
  availableNames: ReadonlySet<string>,
): string[] {
  return (attributes ?? []).filter(
    attribute => attribute.length > 0 && !availableNames.has(attribute),
  );
}

/**
 * Confronta gli attributi configurati con gli attributi disponibili
 * dall'introspezione. Da invocare solo con un'introspezione riuscita:
 * un confronto contro una lista vuota marcherebbe tutto come non valido.
 */
export function diffLayerAttributes(
  snapshot: ILayerAttributeSnapshot,
  availableNames: ReadonlySet<string>,
): ILayerAttributeDiff {
  const invalidIntersectionAttributes = findInvalidAttributes(
    snapshot.intersectionAttributes,
    availableNames,
  );
  const invalidIdentifyAttributes = findInvalidAttributes(
    snapshot.identifyAttributes,
    availableNames,
  );

  const competenceProperty = snapshot.competenceIdentifyingPropertyName;
  const isCompetencePropertyInvalid =
    !!competenceProperty && !availableNames.has(competenceProperty);

  const invalidNames = Array.from(
    new Set([
      ...invalidIntersectionAttributes,
      ...invalidIdentifyAttributes,
      ...(isCompetencePropertyInvalid && competenceProperty
        ? [competenceProperty]
        : []),
    ]),
  );

  return {
    invalidIntersectionAttributes,
    invalidIdentifyAttributes,
    isCompetencePropertyInvalid,
    invalidNames,
    hasInvalid: invalidNames.length > 0,
  };
}

function pruneAliases(
  aliases: Record<string, string> | undefined,
  validAttributes: ReadonlySet<string>,
): Record<string, string> | undefined {
  if (!aliases) return undefined;

  const entries = Object.entries(aliases).filter(([key]) =>
    validAttributes.has(key),
  );
  if (entries.length === Object.keys(aliases).length) return undefined;

  return Object.fromEntries(entries);
}

/**
 * Calcola la patch che rimuove SOLO gli attributi configurati non più
 * disponibili, preservando i validi (e il loro ordine) e gli alias
 * corrispondenti.
 */
export function pruneLayerAttributes(
  snapshot: ILayerAttributeSnapshot,
  availableNames: ReadonlySet<string>,
): ILayerAttributePrunePatch {
  const diff = diffLayerAttributes(snapshot, availableNames);
  const patch: ILayerAttributePrunePatch = {};

  if (diff.invalidIntersectionAttributes.length > 0) {
    patch.intersectionAttributes = (
      snapshot.intersectionAttributes ?? []
    ).filter(attribute => availableNames.has(attribute));
    const prunedAliases = pruneAliases(
      snapshot.intersectionAliases,
      new Set(patch.intersectionAttributes),
    );
    if (prunedAliases) patch.intersectionAliases = prunedAliases;
  }

  if (diff.invalidIdentifyAttributes.length > 0) {
    patch.identifyAttributes = (snapshot.identifyAttributes ?? []).filter(
      attribute => availableNames.has(attribute),
    );
    const prunedAliases = pruneAliases(
      snapshot.identifyAliases,
      new Set(patch.identifyAttributes),
    );
    if (prunedAliases) patch.identifyAliases = prunedAliases;
  }

  if (diff.isCompetencePropertyInvalid) {
    patch.competenceIdentifyingPropertyName = null;
  }

  return patch;
}
